import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useConfirm } from '../../../../shared/components/ConfirmDialog/useConfirm';
import { useProjet } from '../../../administration/hooks/useProjets';
import { useEmployes } from '../../../employes/hooks/useEmployes';
import {
  useContributionsParProjet,
  useCreerContribution,
  useModifierContribution,
  useSupprimerContribution,
} from '../hooks/useContributionsProjet';
import { useContributionsProjetStore } from '../store/contributionsProjet.store';
import {
  modifierContributionSchema,
  type ModifierContributionFormData,
} from '../../application/dto/dtos';

const f = (n: number) => n.toLocaleString('fr-FR') + ' F';

const creerProjetSchema = z.object({
  idEmploye: z.string().min(1, 'Veuillez sélectionner un employé'),
  montant:   z.number({ invalid_type_error: 'Le montant est requis' }).positive('Le montant doit être positif'),
});
type CreerProjetFormData = z.infer<typeof creerProjetSchema>;

export function ContributionsProjetPage() {
  const { id } = useParams<{ id: string }>();
  const confirm = useConfirm();
  const store = useContributionsProjetStore();

  const { data: projet } = useProjet(id);
  const { data: contributions = [], isLoading } = useContributionsParProjet(id);
  const { data: employes = [] } = useEmployes({ actif: true });

  const creer    = useCreerContribution(undefined, id);
  const modifier = useModifierContribution(undefined, id);
  const supprimer = useSupprimerContribution(undefined, id);

  const creerForm = useForm<CreerProjetFormData>({
    resolver: zodResolver(creerProjetSchema),
    defaultValues: { idEmploye: '', montant: 0 },
  });

  const editForm = useForm<ModifierContributionFormData>({
    resolver: zodResolver(modifierContributionSchema),
  });

  const handleCreer = creerForm.handleSubmit(async (data) => {
    if (!id) return;
    await creer.mutateAsync({ ...data, idProjet: id }, {
      onSuccess: () => {
        store.setShowFormProjet(false);
        creerForm.reset();
        toast.success('Contribution créée');
      },
      onError: () => toast.error('Erreur lors de la création'),
    });
  });

  const handleModifier = editForm.handleSubmit(async (data) => {
    if (!store.editId) return;
    await modifier.mutateAsync({ idContribution: store.editId, data }, {
      onSuccess: () => {
        store.setEditId(null);
        toast.success('Contribution modifiée');
      },
      onError: () => toast.error('Erreur lors de la modification'),
    });
  });

  const handleSupprimer = async (idContribution: string, nomEmploye: string) => {
    const ok = await confirm({
      title: 'Supprimer cette contribution ?',
      message: `La contribution de ${nomEmploye} sera supprimée définitivement.`,
      variant: 'danger',
      labelConfirm: 'Supprimer',
    });
    if (!ok) return;
    await supprimer.mutateAsync(idContribution, {
      onSuccess: () => toast.success('Contribution supprimée'),
      onError: () => toast.error('Erreur lors de la suppression'),
    });
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Link
        to={`/paie/administration/projets/${id}`}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-4"
      >
        <ArrowLeft size={14} />
        {projet ? projet.nom : 'Retour'}
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-content">Employés en contribution</h1>
          {projet && <p className="text-sm text-slate-500 mt-0.5">{projet.nom}</p>}
        </div>
        <button
          onClick={() => {
            creerForm.reset();
            store.setShowFormProjet(!store.showFormProjet);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-content-inverted rounded-lg text-sm font-medium hover:bg-primary-hover transition-colors"
        >
          <Plus size={14} />
          Ajouter
        </button>
      </div>

      {store.showFormProjet && (
        <div className="bg-primary-light/30 border border-primary/20 rounded-xl p-5 mb-6">
          <form onSubmit={handleCreer} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Employé *</label>
                <select
                  {...creerForm.register('idEmploye')}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Choisir...</option>
                  {(employes as any[]).map((e: any) => (
                    <option key={e.idEmploye} value={e.idEmploye}>{e.nom} {e.prenoms}</option>
                  ))}
                </select>
                {creerForm.formState.errors.idEmploye && (
                  <p className="text-xs text-danger mt-1">{creerForm.formState.errors.idEmploye.message}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Montant (F) *</label>
                <input
                  type="number"
                  min={1}
                  {...creerForm.register('montant', { valueAsNumber: true })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {creerForm.formState.errors.montant && (
                  <p className="text-xs text-danger mt-1">{creerForm.formState.errors.montant.message}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => store.setShowFormProjet(false)}
                className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={creer.isPending}
                className="px-4 py-1.5 bg-primary text-content-inverted rounded-lg text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
              >
                {creer.isPending ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Chargement...</div>
        ) : contributions.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Aucun employé en contribution sur ce projet</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Employé</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Montant</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Importé le</th>
                <th className="px-5 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {contributions.map(c => (
                <tr key={c.idContribution} className="hover:bg-slate-50/50 transition-colors">
                  {store.editId === c.idContribution ? (
                    <>
                      <td className="px-5 py-3 text-sm text-content">{c.nomEmploye}</td>
                      <td className="px-5 py-2">
                        <input
                          type="number"
                          min={1}
                          {...editForm.register('montant', { valueAsNumber: true })}
                          defaultValue={c.montant}
                          className="border border-slate-200 rounded px-2 py-1 text-sm w-28 text-right focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </td>
                      <td className="px-5 py-2 text-sm text-slate-400">
                        {new Date(c.importeLe).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-5 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={handleModifier}
                            className="p-1.5 text-primary hover:bg-primary-light rounded"
                            disabled={modifier.isPending}
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => store.setEditId(null)}
                            className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-5 py-3 text-sm font-medium text-content">
                        <Link to={`/paie/employes/${c.idEmploye}`} className="hover:text-primary">
                          {c.nomEmploye}
                        </Link>
                      </td>
                      <td className="px-5 py-3 text-sm font-mono tabular-nums text-right text-primary-text font-semibold">
                        {f(c.montant)}
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-400">
                        {new Date(c.importeLe).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => {
                              store.setEditId(c.idContribution);
                              editForm.reset({ montant: c.montant });
                            }}
                            className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary-light rounded transition-colors"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => handleSupprimer(c.idContribution, c.nomEmploye)}
                            className="p-1.5 text-slate-400 hover:text-danger hover:bg-danger/10 rounded transition-colors"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {contributions.length > 0 && (
        <div className="mt-3 flex justify-end">
          <span className="text-sm text-slate-500">
            Total : <span className="font-semibold text-content">
              {f(contributions.reduce((s, c) => s + c.montant, 0))}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}
